/**
 * AccessDenied - Página de acesso negado
 * 
 * Exibida quando um usuário tenta acessar uma rota sem permissão.
 * Renderiza dentro do TenantLayout, mantendo sidebar e header disponíveis.
 * 
 * Casos de uso:
 * - Agente tentando acessar /tenant/settings
 * - Supervisor sem permissão tentando acessar /tenant/agents
 * - Qualquer acesso não autorizado detectado pelo PermissionGuard
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldX, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export default function AccessDenied() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Rota de origem (se disponível)
  const fromPath = location.state?.from || 'desconhecida';

  return (
    <div className="flex items-center justify-center py-12">
      <Card className="w-full max-w-md text-center">
        <CardHeader className="space-y-4">
          {/* Ícone de bloqueio */}
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <ShieldX className="h-8 w-8 text-destructive" />
          </div>
          
          {/* Título */}
          <CardTitle className="text-2xl font-bold">
            {t('accessDenied.title', 'Acesso Negado')}
          </CardTitle>
          
          {/* Descrição */}
          <CardDescription className="text-base">
            {t(
              'accessDenied.description', 
              'Você não tem permissão para acessar esta página. Entre em contato com o administrador se precisar de acesso.'
            )}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Informação técnica - sempre exibir para facilitar suporte */}
          <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
            <p className="font-mono">
              {t('accessDenied.attemptedRoute', 'Rota tentada')}: {fromPath}
            </p>
          </div>
          
          {/* Botão de voltar - sidebar já disponível para outras navegações */}
          <div className="flex justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('accessDenied.goBack', 'Voltar')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
